import { useEffect } from 'react';

/**
 * Hook pour binder des raccourcis clavier globaux.
 *
 * Chaque raccourci :
 *   - key         : valeur de event.key (ex. 'Escape', 's', '/', '?', '1', 'Enter')
 *   - ctrl?       : true si Ctrl OU Cmd (Mac) doit être pressé
 *   - shift?      : true si Shift doit être pressé
 *   - alt?        : true si Alt doit être pressé
 *   - handler     : fonction appelée (reçoit l'event)
 *   - allowInInput: si true, le raccourci marche aussi quand le focus est dans un input/textarea
 *
 * Exemple :
 *   useKeyboardShortcuts([
 *     { key: 'Escape', handler: () => navigate('/agent/clients') },
 *     { key: 's', ctrl: true, handler: () => save() },
 *     { key: '/', handler: () => inputRef.current?.focus() },
 *     { key: '?', allowInInput: true, handler: () => setHelp(true) },
 *   ], [navigate, save]);
 */
export function useKeyboardShortcuts(shortcuts, deps = []) {
  useEffect(() => {
    function handler(e) {
      const tag = e.target?.tagName;
      const isInput =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        e.target?.isContentEditable;

      for (const sc of shortcuts) {
        if (!sc || typeof sc.handler !== 'function') continue;
        if (isInput && !sc.allowInInput) continue;

        // Matching de la touche (insensible à la casse pour les lettres simples)
        const eventKey = e.key;
        const scKey = sc.key;
        const keyMatches =
          eventKey === scKey ||
          (scKey.length === 1 && eventKey.length === 1 &&
            eventKey.toLowerCase() === scKey.toLowerCase());
        if (!keyMatches) continue;

        // Ctrl/Cmd (on accepte les deux pour la portabilité Mac/Windows)
        if (!!sc.ctrl !== (e.ctrlKey || e.metaKey)) continue;
        if (!!sc.shift !== e.shiftKey) continue;
        if (!!sc.alt !== e.altKey) continue;

        e.preventDefault();
        sc.handler(e);
        break;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Modal d'aide qui affiche la liste des raccourcis disponibles.
 * Le parent contrôle son ouverture/fermeture via `open` / `onClose`.
 *
 * Props :
 *   open       : bool
 *   onClose    : fn
 *   shortcuts  : Array<{ combo: string, label: string }>
 */
export function ShortcutsHelpModal({ open, onClose, shortcuts }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Raccourcis clavier"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#FDFCFA', padding: 28, borderRadius: 14, minWidth: 360,
          maxWidth: 560, boxShadow: '0 12px 36px rgba(0,0,0,0.22)',
          border: '1px solid #E0DDD8',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 17, color: '#2D2D2D', marginBottom: 6 }}>
          Raccourcis clavier
        </div>
        <div style={{ fontSize: 13, color: '#8A8A8A', marginBottom: 18 }}>
          Pour aller plus vite pendant l&apos;appel.
        </div>
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <tbody>
            {shortcuts.map((sc, i) => (
              <tr key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid #F0EDE6' }}>
                <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                  <kbd style={{
                    padding: '3px 9px', background: '#F5EFE6',
                    border: '1px solid #D1CCC2', borderRadius: 5,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 12, color: '#5A3C1F', fontWeight: 600,
                  }}>
                    {sc.combo}
                  </kbd>
                </td>
                <td style={{ padding: '10px 8px', color: '#2D2D2D' }}>{sc.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ textAlign: 'right', marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: '9px 22px', background: '#D4724A', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
          }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Petit indicateur discret en bas à droite : "? raccourcis".
 * Cliquable pour ouvrir la modal d'aide.
 */
export function ShortcutsHint({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Voir les raccourcis clavier"
      style={{
        position: 'fixed', bottom: 16, right: 16, zIndex: 900,
        padding: '6px 12px', borderRadius: 20,
        background: 'rgba(255,255,255,0.92)', border: '1px solid #E0DDD8',
        color: '#8A8A8A', fontSize: 12, fontWeight: 500, cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      <kbd style={{
        padding: '1px 6px', background: '#F5EFE6', border: '1px solid #D1CCC2',
        borderRadius: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11, color: '#5A3C1F', fontWeight: 600,
      }}>?</kbd>
      raccourcis
    </button>
  );
}
